import { Controller, FieldValues, UseControllerProps } from "react-hook-form";
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback } from "react";

interface Props<T extends FieldValues>
  extends Omit<
      AutocompleteProps<string | number, boolean, boolean, boolean>,
      "renderInput" | "label" | "defaultValue"
    >,
    UseControllerProps<T> {
  label?: string;
  options: (string | number)[];
  testId?: string;
  optional?: boolean;
  allowNewValue?: boolean;
  newOptionText?: string;
  placeholder?: string;
}

export const RHFAutocomplete = <T extends FieldValues>({
  name,
  label,
  testId,
  options,
  control,
  optional = false,
  allowNewValue = false,
  newOptionText,
  placeholder,
  ...props
}: Props<T>) => {
  const Label = (
    <>
      {label}
      {optional ? (
        <Typography color="textSecondary" component="span" variant="body3">
          {" "}
          (optional)
        </Typography>
      ) : (
        ""
      )}
    </>
  );

  const getOptions = useCallback(
    (val: string) => {
      const isExistingOption = options.some((x) => x === val);
      return [
        ...options,
        ...(allowNewValue && !isExistingOption && val.length >= 3
          ? [`${val} (${newOptionText})`]
          : []),
      ];
    },
    [allowNewValue, newOptionText, options]
  );

  return (
    <Box>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <Autocomplete
              freeSolo={allowNewValue}
              options={getOptions(field.value ?? "") ?? []}
              autoHighlight
              value={field.value}
              inputValue={allowNewValue ? field.value : undefined}
              onChange={(_, value) => {
                if (allowNewValue && value === null) return;
                if (allowNewValue && typeof value === "string") {
                  return field.onChange(
                    value?.replace(` (${newOptionText})`, "")
                  );
                }
                field.onChange(value);
              }}
              onInputChange={(_, value) => {
                if (allowNewValue) {
                  field.onChange(value ?? "");
                }
              }}
              disablePortal
              renderInput={({ InputLabelProps, ...rest }) => {
                return (
                  <TextField
                    {...rest}
                    label={Label}
                    variant="outlined"
                    placeholder={placeholder || label}
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      htmlInput: {
                        ...rest.inputProps,
                        "data-testid": testId || "autocomplete",
                      },
                    }}
                  />
                );
              }}
              {...props}
            />
          );
        }}
      />
    </Box>
  );
};
